import { useMemo } from "react";
import type { Vector3 } from "three";
import { CasedLine } from "./CasedLine";
import { GUIDE_DASH_MM, GUIDE_GAP_MM, guideStyle } from "./dragTheme";

const HELPER_USER_DATA = { userData: { isDragHelper: true } };

/**
 * Guide lines sit above the movement outline, which occupies 999/1000. Both
 * layers of a cased stroke need their own slot, so each stroke claims two.
 */
const GUIDE_RENDER_ORDER = 1001;

/**
 * A guide segment between two explicit endpoints: a panel's center line
 * spanning that panel, or a 45° diagonal. Explicit endpoints rather than a
 * center and a half length, so a line through a snap center that is off the
 * panel's middle still ends exactly at the panel's edges.
 */
export interface GuideSegment {
  /** Segment start in world space. */
  start: Vector3;
  /** Segment end in world space. */
  end: Vector3;
  /** Whether the dragged part is currently snapped to this line. */
  snapped: boolean;
  /** Stable key for React. */
  key: string;
}

/**
 * A single guide line: a white core over a dark casing, so it stays readable
 * against any panel colour the user picks. See `CasedLine`.
 */
const SegmentLine = ({ start, end, snapped }: Omit<GuideSegment, "key">) => {
  const points = useMemo<[Vector3, Vector3]>(() => [start, end], [start, end]);
  return (
    <CasedLine
      points={points}
      style={guideStyle(snapped)}
      dashSize={GUIDE_DASH_MM}
      gapSize={GUIDE_GAP_MM}
      renderOrder={GUIDE_RENDER_ORDER}
    />
  );
};

/**
 * Renders the guide lines a draggable part can snap to: the center lines of
 * each panel it may sit on and any diagonal snap lines. Each line is
 * highlighted while the part is snapped to it.
 */
export const DragGuideLines = ({ segments }: { segments: GuideSegment[] }) => {
  return (
    <group {...HELPER_USER_DATA}>
      {segments.map(({ key, ...segment }) => (
        <SegmentLine key={key} {...segment} />
      ))}
    </group>
  );
};

export default DragGuideLines;
